import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Modal,
  Box,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  CircularProgress,
  IconButton,
} from '@mui/material';
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import { validatePersonOrEntityName } from "utils/crmValidation";
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from 'auth/Auth';
import CustomFieldsSection from 'components/CustomFields/CustomFieldsSection';
import { useTravelUi } from 'hooks/useTravelUi';
import { useEditionFields } from 'hooks/useEditionFields';
import LeadPropertyRequirements from 'layouts/RealEstate/LeadPropertyRequirements';
import { CountrySelect, StateSelect, CitySelect } from 'components/GeographySelect';

// Add these imports
import { Alert, Snackbar } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import colors from "assets/theme/base/colors";
import { frontendLogger } from 'utils/logger';
import { useTheme } from '@mui/material/styles';
import AddressAutocompleteInput from "../../layouts/Travel/trips/stepForm/fields/AddressAutocompleteInput";

// Hidden from core CRM UI — keep form state/API payload; set true to show again.
const SHOW_HIDDEN_CORE_CRM_FIELDS = false;

const cleanMsg = (m) => String(m ?? '').replace(/^Value error,\s*/i, '');

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Ignores common separators, then requires 7-15 digits with an optional leading +
const isValidPhone = (v) =>
    /^\+?\d{7,15}$/.test(String(v).trim().replace(/[\s\-().]/g, ''));

const PHONE_ERROR = 'Please enter a valid phone number (digits, optionally starting with +).';
const DEFAULT_FORM_DATA = {
    lead_status: 'New',
    lead_rating: 'Not Selected',
    lead_source: 'Not Selected',
    salutation: 'Not Selected',
    designation: '',
    utm_key: '',
    trip_id: null,
    website_reference: '',
    type: '',
    creator_type: '',
    language: 'English',
    country: '',
    state: '',
    city: '',
    email: '',
    phone: '',
    mobile: '',
    first_name: '',
    last_name: '',
    company_name: '',
}

/** Strip server-only keys and seed the create/clone form. */
const buildLeadFormData = (initialData) => {
  if (!initialData) return { ...DEFAULT_FORM_DATA };
  const {
    id,
    created_by,
    updated_by,
    created_by_name,
    updated_by_name,
    assigned_to_name,
    owner_queue_id,
    trip,
    last_activity,
    created_at,
    updated_at,
    ...rest
  } = initialData;
  return {
    ...DEFAULT_FORM_DATA,
    ...rest,
    salutation: rest.salutation || 'Not Selected',
    lead_rating: rest.lead_rating || 'Not Selected',
    lead_source: rest.lead_source || 'Not Selected',
    lead_status: rest.lead_status || 'New',
    email: rest.email || '',
    phone: rest.phone || '',
    mobile: rest.mobile || '',
    first_name: rest.first_name || '',
    last_name: rest.last_name || '',
    company_name: rest.company_name || '',
  };
};

const LeadFormModal = ({
  isOpen,
  onClose,
  onSave,
  agents,
  currencies,
  defaultCurrency,
  user,
  title = 'Add New Lead',
  initialData = null,
}) => {
  // v2 dialog forms trim margin="normal"; the address row wrappers follow.
  const isV2 = useTheme()?.pearlUi === 'v2';

  const navigate = useNavigate();

  const [formData, setFormData] = useState(() => buildLeadFormData(initialData));
    const [selectedTrip, setSelectedTrip] = useState(null);

    // Travel UI needs the TRAVEL edition and the company travel toggle.
    const { showTravel: showTripField } = useTravelUi();

    // Real Estate lead fields are tagged to the REAL_ESTATE edition.
    const { isFieldVisible } = useEditionFields();
    const showPropertyRequirements = isFieldVisible('leads', 're_purpose');

  // const [selectedAgent, setSelectedAgent] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(user ? {
    id: user.id,
    username: user.username
  } : null);

  const formRef = useRef(null);

  const [selectedProducts, setSelectedProducts] = useState([]);
  const [currency, setCurrency] = useState(defaultCurrency);
  const [formErrors, setFormErrors] = useState({});
  const [products, setProducts] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const { axiosInstance } = useAuth();
  const [isSaving, setIsSaving] = useState(false); // New state for save button loading

  const [toast, setToast] = useState({
    open: false,
    message: '',
    type: 'info', // 'error', 'warning', 'info', 'success'
    icon: null
  });

  const [customFields, setCustomFields] = useState([]);
  const customFieldsRef = useRef(null);

  // Travel on → trip packages; travel off → CRM products. Same interested_products field.
  const fetchInterestOptions = async () => {
    try {
      if (showTripField) {
        const response = await axiosInstance.get('/trip-packages?status=active');
        const packages = Array.isArray(response.data) ? response.data : [];
        setProducts(
          packages.map((pkg) => ({
            id: pkg.id,
            name: pkg.title || pkg.name || `Package #${pkg.id}`,
          }))
        );
      } else {
        const response = await axiosInstance.get('/products/');
        setProducts(Array.isArray(response.data) ? response.data : []);
      }
    } catch (error) {
      console.error(
        showTripField ? 'Error fetching trip packages:' : 'Error fetching products:',
        error
      );
      setProducts([]);
    }
  };

  const fetchTrips = async (searchTerm = '') => {
    if (!showTripField) {
      setTrips([]);
      return;
    }
    try {
      setLoadingTrips(true);
      const response = await axiosInstance.get(`/trips/?skip=0&limit=100&search=${searchTerm}`);
      setTrips(response.data || []);
    } catch (error) {
      console.error('Error fetching trips:', error);
      setTrips([]);
    } finally {
      setLoadingTrips(false);
    }
  };


  useEffect(() => {
    if (user) {
      const currentUserAsAgent = {
        id: user.id,
        username: user.username
      };
      setSelectedAgent(currentUserAsAgent);
    }
  }, [user]);

    // Reset only when the modal opens. Do not depend on initialData — parents
    // often pass an inline object, which would wipe email/phone after a failed
    // save (matches ContactFormModal).
    useEffect(() => {
        if (!isOpen) return;
        const seed = buildLeadFormData(initialData);
        setFormData(seed);
        setFormErrors({});
        setSelectedProducts([]);
        setSelectedTrip(null);
        const currencyCode = seed.currency || defaultCurrency?.code;
        const matchedCurrency =
          (currencies || []).find((c) => c.code === currencyCode) || defaultCurrency;
        setCurrency(matchedCurrency);
        if (initialData?.assigned_to) {
          const agent =
            (agents || []).find((a) => a.id === initialData.assigned_to) ||
            { id: initialData.assigned_to, username: initialData.assigned_to_name || user?.username };
          setSelectedAgent(agent);
        } else if (user) {
          setSelectedAgent({ id: user.id, username: user.username });
        }
    }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reload options when the mode changes
    useEffect(() => {
        if (!isOpen) return;
        fetchInterestOptions();
        if (showTripField) {
            fetchTrips();
        } else {
            setTrips([]);
            setSelectedTrip(null);
            if (!initialData?.trip_id) {
              setFormData((prev) => ({ ...prev, trip_id: null }));
            }
        }
    }, [isOpen, showTripField]); // eslint-disable-line react-hooks/exhaustive-deps

    // Restore interested products / trip after option lists load (clone).
    useEffect(() => {
      if (!isOpen || !initialData) return;
      const names = initialData.interested_products;
      if (Array.isArray(names) && names.length && products.length) {
        setSelectedProducts(products.filter((p) => names.includes(p.name)));
      }
      if (initialData.trip_id && trips.length) {
        const trip = trips.find((t) => t.id === initialData.trip_id);
        if (trip) setSelectedTrip(trip);
      }
    }, [isOpen, initialData, products, trips]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        const cleanedValue =
            name === 'postal_code' && (value === 'None' || value === null) ? '' : value;
        setFormData((prev) => ({ ...prev, [name]: cleanedValue }));

        setFormErrors((prev) => {
            const next = { ...prev, [name]: '' };
            // Email and phone share one "provide at least one" error; clear both together
            if ((name === 'email' || name === 'phone') && prev.email && prev.email === prev.phone) {
                next.email = '';
                next.phone = '';
            }
            return next;
        });
    };

    // Phone format check runs when the user leaves the field
    const handlePhoneBlur = (e) => {
        const value = e.target.value.trim();
        if (value && !isValidPhone(value)) {
            setFormErrors((prev) => ({ ...prev, phone: PHONE_ERROR }));
        }
    };

    const mapApiValidationErrors = (detail) => {
        const fallback = 'Validation failed. Please review the highlighted fields.';

        if (!detail) {
            return { fieldErrors: {}, message: fallback };
        }

        if (typeof detail === 'string') {
            return { fieldErrors: {}, message: cleanMsg(detail) };
        }

        // Structured single error, e.g. { field: 'email', message: '...' }
        if (!Array.isArray(detail) && typeof detail === 'object') {
            const message = cleanMsg(detail.message || detail.msg || fallback);
            return {
                fieldErrors: detail.field ? { [detail.field]: message } : {},
                message,
            };
        }

        if (Array.isArray(detail)) {
            const fieldErrors = {};
            const messages = detail.map((entry) => {
                // Top-level field name; skips "body" and list indexes (e.g. interested_products.0)
                const fieldName = Array.isArray(entry.loc)
                    ? entry.loc.filter((p) => p !== 'body')[0]
                    : entry.field;
                const message = cleanMsg(entry.msg || entry.message || 'Invalid value');

                if (fieldName === undefined) {
                    // Model-level error, e.g. "Either email or phone number must be provided."
                    if (/email or phone/i.test(message)) {
                        fieldErrors.email = fieldErrors.email || message;
                        fieldErrors.phone = fieldErrors.phone || message;
                    }
                } else if (!fieldErrors[fieldName]) {
                    fieldErrors[fieldName] = message;
                }
                return message;
            });

            return { fieldErrors, message: messages.join(' ') };
        }

        return { fieldErrors: {}, message: fallback };
    };

  const handleSaveLead = async (e) => {
    e.preventDefault();
    const isStandardFormValid = validateForm();
    const isCustomFieldsValid = customFieldsRef.current?.validateAllFields() ?? true;

    if (isStandardFormValid && isCustomFieldsValid) {
      setIsSaving(true);
      try {
        const leadData = {
            ...formData,
            salutation: formData.salutation === "Not Selected" ? null : formData.salutation,
            lead_rating: formData.lead_rating === "Not Selected" ? null : formData.lead_rating,
            lead_source: formData.lead_source === "Not Selected" ? null : formData.lead_source,
            country: formData.country || null,
            state: formData.state || null,
            city: formData.city || null,
            // Blank email must be null — "" fails EmailStr and used to crash the page via snackbar.
            email: formData.email?.trim() || null,
            phone: formData.phone?.trim() || null,
            mobile: formData.mobile?.trim() || null,
            website: formData.website?.trim() || null,
            number_of_employees:
              formData.number_of_employees === "" || formData.number_of_employees == null
                ? null
                : Number(formData.number_of_employees),
            annual_revenue:
              formData.annual_revenue === "" || formData.annual_revenue == null
                ? null
                : Number(formData.annual_revenue),
            assigned_to: selectedAgent?.id || null,
            interested_products: selectedProducts.map(p => p.name),
            currency: currency?.code,
            created_by: user?.id,
            updated_by: user?.id,
            postal_code: formData.postal_code || "",  // Convert null/undefined to empty string
          };

          const response = await onSave(leadData);

          // Log lead creation
          if (response && response.id) {
              try {
                  await frontendLogger.logCreate('Lead', {
                      id: response.id,
                      name: response.full_lead_name || response.name || `${formData.first_name} ${formData.last_name}`,
                      created_by: user?.id,
                      created_at: response.created_at || new Date().toISOString(),
                      company: formData.company_name,
                      email: formData.email,
                      phone: formData.phone,
                      lead_source: formData.lead_source,
                      lead_status: formData.lead_status,
                      user: user?.username,
                      action: 'create',
                      timestamp: new Date().toISOString()
                  });
              } catch (logError) {
                  console.error('Error logging lead creation:', logError);
              }
          }

          // Reset form data
          setSelectedProducts([]);
          setCurrency(defaultCurrency);
          setFormErrors({});

          // Close the modal
          onClose();

          // Navigate to the lead detail page
          if (response && response.id) {
            navigate(`/leads/${response.id}`);
          }
      } catch (error) {
        console.error('Error creating lead:', error);

        // Best-effort logging — a logger failure must not replace the real create error UI.
        try {
          await frontendLogger.logError(error, {
            context: 'Lead creation',
            formData: formData,
            user: user?.username,
            action: 'create',
            timestamp: new Date().toISOString()
          });
        } catch (logError) {
          console.error('Error logging lead creation failure:', logError);
        }

        // Handle validation errors (422 status code)
        if (error.response?.status === 422) {
          const { fieldErrors, message } = mapApiValidationErrors(error.response.data.detail);
          if (Object.keys(fieldErrors).length > 0) {
            setFormErrors((prev) => ({ ...prev, ...fieldErrors }));
            scrollToFirstError();
          }
          setToast({
            open: true,
            message,
            type: 'error',
            icon: <ErrorOutlineIcon />
          });
        } else {
          const detail = error.response?.data?.detail;
            const message = typeof detail === 'string'
                ? cleanMsg(detail)
                : Array.isArray(detail)
                    ? detail.map((entry) => cleanMsg(entry?.msg || entry?.message || String(entry))).join(' ')
                    : 'Error creating lead. Please try again.';

          // Duplicate email → highlight the email field and jump to it
          if (
              (error.response?.status === 409 || error.response?.status === 400) &&
              typeof detail === 'string' &&
              detail.toLowerCase().includes('email')
          ) {
            setFormErrors((prev) => ({ ...prev, email: detail }));
            scrollToFirstError();
          }

          setToast({
            open: true,
            message,
            type: 'error',
            icon: <ErrorOutlineIcon />
          });
        }
      }
      finally {
        setIsSaving(false); // Stop loading
      }
    } else {
      scrollToFirstError();
      if (isStandardFormValid && !isCustomFieldsValid) {
        setToast({
          open: true,
          message: 'Please complete all required custom fields.',
          type: 'warning',
          icon: <ErrorOutlineIcon/>
        });
      }
    }
  };

    const handleClose = (_, reason) => {
        if (isSaving || reason === 'backdropClick') return;
        setSelectedProducts([]);
        setCurrency(defaultCurrency);
        setFormErrors({});
        onClose();
    };


  const validationMessages = {
    required: [
      "This field is required to proceed.",
      "Please complete this required field.",
      "This information is necessary to continue.",
      "Please provide this required information.",
    ],
    invalid: [
      "Please enter a valid format for this field.",
      "The provided input format is incorrect.",
      "Please check and correct the format.",
      "This entry doesn't match the required format.",
    ],
    specific: {
      email: [
        "Please enter a valid email address (e.g., name@company.com).",
        "Email format is invalid. Please verify and try again.",
      ],
      phone: [
        "Please enter a valid phone number format (+XX-XXX-XXX-XXXX).",
        "Phone number format is incorrect. Please check and retry.",
      ],
      website: [
        "Please enter a valid website URL (e.g., www.example.com).",
        "Website URL format is invalid. Please verify and try again.",
      ],
      number: [
        "Please enter a valid numerical value.",
        "This field accepts numbers only. Please revise your entry.",
      ],
      currency: [
        "Please enter a valid monetary amount.",
        "Currency value is invalid. Please check and retry.",
      ]
    },
    combined: {
      emailPhone: [
        "Please provide either an email address or phone number.",
        "Contact information (email or phone) is required.",
      ],
      validation: [
        "Please review and correct the highlighted fields.",
        "Some fields require your attention before proceeding.",
      ]
    }
  };

  // Usage in validateForm:
  const validateForm = () => {
    const errors = {};

    const getRandomMessage = (type) => {
      const messages = validationMessages[type];
      return messages[Math.floor(Math.random() * messages.length)];
    };

    const getSpecificMessage = (field) => {
      const messages = validationMessages.specific[field];
      return messages[Math.floor(Math.random() * messages.length)];
    };

    // Required field validations
    if (!formData.last_name?.trim()) {
      errors.last_name = getRandomMessage('required');
    } else {
      const lastNameErr = validatePersonOrEntityName(formData.last_name, 'Last name');
      if (lastNameErr) errors.last_name = lastNameErr;
    }

    if (formData.first_name?.trim()) {
      const firstNameErr = validatePersonOrEntityName(formData.first_name, 'First name');
      if (firstNameErr) errors.first_name = firstNameErr;
    }

    if (formData.company_name?.trim()) {
      const companyErr = validatePersonOrEntityName(formData.company_name, 'Company name');
      if (companyErr) errors.company_name = companyErr;
    }

    // Email/Phone validation
    if (!formData.email?.trim() && !formData.phone?.trim()) {
      const message = validationMessages.combined.emailPhone[0];
      errors.email = message;
      errors.phone = message;
    }

    // Email format validation
      if (formData.email?.trim() && !EMAIL_RE.test(formData.email.trim())) {
          errors.email = getSpecificMessage('email');
      }

    // Phone validation:
      if (formData.phone?.trim() && !isValidPhone(formData.phone)) {
          errors.phone = PHONE_ERROR;
      }

    // Website validation
    const isValidWebsite = (value) => {
       const v = value.trim();
       if (!v || /\s/.test(v)) return false;
       try {
          const url = new URL(/^https?:\/\//i.test(v) ? v : `https://${v}`);
          return url.hostname.includes('.') && !url.hostname.endsWith('.');
       } catch {
          return false;
       }
    };

      // in validateForm
      if (formData.website?.trim() && !isValidWebsite(formData.website)) {
          errors.website = getSpecificMessage('website');
      }

    // Number of employees validation
      if (formData.number_of_employees !== undefined && formData.number_of_employees !== '') {
          const n = Number(formData.number_of_employees);
          if (!Number.isInteger(n) || n < 0) {
              errors.number_of_employees = 'Please enter a whole number of 0 or more.';
          }
      }

    // Annual revenue validation
    if (formData.annual_revenue !== undefined && formData.annual_revenue !== '') {
      const revenue = Number(formData.annual_revenue);
    if (!Number.isFinite(revenue)) {
        errors.annual_revenue = getSpecificMessage('currency');
      } else if (revenue < 0) {
        errors.annual_revenue = 'Annual revenue cannot be less than 0.';
      }
    }

    setFormErrors(errors);

    // Show toast if there are errors
    if (Object.keys(errors).length > 0) {
      setToast({
        open: true,
        message: validationMessages.combined.validation[0],
        type: 'warning',
        icon: <ErrorOutlineIcon />
      });
      return false;
    }

    return true;
  };

  const handleCurrencyChange = (_, newValue) => {
    setCurrency(newValue);
  };

  const handleProductChange = (_, newValue) => {
    setSelectedProducts(newValue);
  };

  // Add useEffect to fetch custom fields
  useEffect(() => {
    const fetchCustomFields = async () => {
      try {
        const response = await axiosInstance.get('/custom-fields/leads');
        setCustomFields(response.data || []);
      } catch (error) {
        console.error('Error fetching custom fields:', error);
        setCustomFields([]);
      }
    };

    fetchCustomFields();
  }, []);

  // Add handlers for custom fields
  const handleFieldUpdate = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFieldError = (field, error) => {
    if (error) {
      setFormErrors(prev => ({
        ...prev,
        [field]: error
      }));
    } else {
      setFormErrors(prev => {
        const { [field]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const [scrollTick, setScrollTick] = useState(0);
  const scrollToFirstError = () => setScrollTick((t) => t + 1);

  useEffect(() => {
      if (!scrollTick) return;
      const form = formRef.current;
      if (!form) return;
      const first = Array.from(form.querySelectorAll('[aria-invalid="true"]'))
        .find((el) => el.offsetParent !== null);
      if (!first) return;
      first.scrollIntoView({ behavior: 'smooth', block: 'center' });
      first.focus({ preventScroll: true });
  }, [scrollTick]);

  return (
    <>
      <Modal
        open={isOpen}
        onClose={handleClose}
        aria-labelledby="lead-form-modal-title"
      >
        <Box sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '90%', sm: '80%', md: '70%', lg: '60%' },
          maxWidth: 800,
          maxHeight: '90vh',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}>
          {/* Header - Fixed position */}
          <Box className="pearl-popup-header"
            sx={{
              backgroundColor: colors.popupHeader.background,
              px: 4,
              py: 3,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <MDTypography
              id="lead-form-modal-title"
              variant="h6"
              component="h2"
              sx={{ color: '#FFFFFF' }}  // Explicitly set white color
            >
              {title}
            </MDTypography>
            <IconButton
              aria-label="Close"
              onClick={handleClose}
              sx={{ color: '#FFFFFF', ml: 1 }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Scrollable content area */}
          <Box
            sx={{
              overflowY: 'auto',
              p: 4,
              '&::-webkit-scrollbar': {
                width: '0.4em'
              },
              '&::-webkit-scrollbar-track': {
                background: '#f1f1f1',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#888',
                borderRadius: '4px'
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: '#555'
              }
            }}
          >
            <form ref={formRef} onSubmit={handleSaveLead} noValidate>
              <MDTypography variant="caption" color="text" display="block" mb={1}>
                <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>*</Box>
                {' '}Required field. Provide at least one of Email or Phone.
              </MDTypography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  {/* Salutation Select */}
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Salutation</InputLabel>
                    <Select
                      name="salutation"
                      label="Salutation"
                      value={formData.salutation}
                      onChange={handleInputChange}

                    >
                      <MenuItem value="Not Selected"></MenuItem>
                      <MenuItem value="Ms.">Ms.</MenuItem>
                      <MenuItem value="Mr.">Mr.</MenuItem>
                      <MenuItem value="Mrs.">Mrs.</MenuItem>
                      <MenuItem value="Dr.">Dr.</MenuItem>
                      <MenuItem value="Prof.">Prof.</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="first_name"
                    label="First Name"
                    variant="outlined"
                    margin="normal"
                    value={formData.first_name || ''}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="last_name"
                    label="Last Name"
                    variant="outlined"
                    margin="normal"
                    required
                    value={formData.last_name || ''}
                    onChange={handleInputChange}
                    error={!!formErrors.last_name}
                    helperText={formErrors.last_name}
                    sx={{
                      '& .MuiFormLabel-asterisk': { color: 'text.primary' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: SHOW_HIDDEN_CORE_CRM_FIELDS ? undefined : 'none' }}>
                  <TextField
                    fullWidth
                    name="designation"
                    label="Designation"
                    variant="outlined"
                    margin="normal"
                    value={formData.designation || ''}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="email"
                    label="Email"
                    variant="outlined"
                    margin="normal"
                    required={!formData.phone?.trim()}
                    value={formData.email || ''}
                    onChange={handleInputChange}
                    error={!!formErrors.email}
                    helperText={
                      formErrors.email ||
                      (initialData && !formData.phone?.trim()
                        ? 'Enter a new email (or phone) — originals cannot be copied'
                        : (!formData.phone?.trim() ? 'Email or phone is required' : ''))
                    }
                    sx={{
                      '& .MuiFormLabel-asterisk': { color: 'text.primary' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="phone"
                    label="Phone"
                    variant="outlined"
                    margin="normal"
                    required={!formData.email?.trim()}
                    value={formData.phone || ''}
                    onChange={handleInputChange}
                    onBlur={handlePhoneBlur}
                    error={!!formErrors.phone}
                    helperText={
                      formErrors.phone
                      || [
                        initialData && !formData.email?.trim()
                          ? 'Enter a new phone (or email) — originals cannot be copied.'
                          : (!formData.email?.trim() ? 'Email or phone is required.' : null),
                        'Digits, optionally starting with +. Spaces, dashes and brackets are fine.',
                      ].filter(Boolean).join(' ')
                    }
                    placeholder="+1234567890"
                    sx={{
                      '& .MuiFormLabel-asterisk': { color: 'text.primary' },
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="mobile"
                    label="Mobile"
                    variant="outlined"
                    margin="normal"
                    value={formData.mobile || ''}
                    onChange={handleInputChange}
                    error={!!formErrors.mobile}
                    helperText={formErrors.mobile}
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: SHOW_HIDDEN_CORE_CRM_FIELDS ? undefined : 'none' }}>
                  <TextField
                    fullWidth
                    name="company_name"
                    label="Company Name"
                    variant="outlined"
                    margin="normal"
                    value={formData.company_name || ''}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDBox mt={isV2 ? 1 : 2} mb={isV2 ? 0.5 : 1}>
                    <AddressAutocompleteInput
                        label="Address"
                        placeholder="Enter address"
                        value={formData.address || ''}
                        onChange={(nextAddress) => {
                          setFormData((prev) => ({ ...prev, address: nextAddress }));
                          setFormErrors((prev) => ({ ...prev, address: '' }));
                        }}
                        error={Boolean(formErrors.address)}
                        errorMessage={formErrors.address}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDBox mt={isV2 ? 1 : 2} mb={isV2 ? 0.5 : 1}>
                    <CountrySelect
                      label="Country"
                      value={formData.country || ''}
                      size="small"
                      onChange={(nextCountry) => {
                        setFormData((prev) => ({
                          ...prev,
                          country: nextCountry || '',
                          state: '',
                          city: '',
                        }));
                      }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDBox mt={isV2 ? 1 : 2} mb={isV2 ? 0.5 : 1}>
                    <StateSelect
                      label="State"
                      country={formData.country || ''}
                      value={formData.state || ''}
                      size="small"
                      onChange={(nextState) => {
                        setFormData((prev) => ({
                          ...prev,
                          state: nextState || '',
                          city: '',
                        }));
                      }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MDBox mt={isV2 ? 1 : 2} mb={isV2 ? 0.5 : 1}>
                    <CitySelect
                      label="City"
                      country={formData.country || ''}
                      state={formData.state || ''}
                      value={formData.city || ''}
                      size="small"
                      onChange={(nextCity) => {
                        setFormData((prev) => ({
                          ...prev,
                          city: nextCity || '',
                        }));
                      }}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="postal_code"
                    label="Postal Code"
                    variant="outlined"
                    margin="normal"
                    type="number"
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="website"
                    label="Website"
                    variant="outlined"
                    margin="normal"
                    onChange={handleInputChange}
                    error={!!formErrors.website}
                    helperText={formErrors.website}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Industry</InputLabel>

                    <Select name="industry" label="Industry" value={formData.industry ?? ''} onChange={handleInputChange}>

                      <MenuItem value=""><em>None</em></MenuItem>
                      {[
                        "Automotive", "Banking", "Construction", "Education",
                        "Financial Services", "Government", "Healthcare", "Manufacturing",
                        "Media", "Retail", "Technology", "Travel", "Other"
                      ].map((industry) => (
                        <MenuItem key={industry} value={industry}>{industry}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                      fullWidth
                      name="annual_revenue"
                      label="Annual Revenue"
                      variant="outlined"
                      margin="normal"
                      type="number"
                      inputProps={{ min: 0 }}
                      onChange={handleInputChange}
                      error={!!formErrors.annual_revenue}
                      helperText={formErrors.annual_revenue}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    options={currencies}
                    getOptionLabel={(option) => `${option.name} (${option.code})`}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Currency"
                        variant="outlined"
                        margin="normal"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '45px',
                            alignItems: 'center'
                          },
                          '& .MuiInputLabel-outlined': {
                            transform: 'translate(14px, 12px) scale(1)'
                          },
                          '& .MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)'
                          }
                        }}
                        fullWidth
                      />
                    )}
                    value={currency ?? null}
                    onChange={handleCurrencyChange}
                    isOptionEqualToValue={(option, value) => option.code === value.code}
                    renderOption={(props, option) => (
                      <li {...props}>
                        {option.name} ({option.code})
                      </li>
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="number_of_employees"
                    label="Number of Employees"
                    variant="outlined"
                    margin="normal"
                    type="number"
                    inputProps={{ min: 0 }}
                    onChange={handleInputChange}
                    error={!!formErrors.number_of_employees}
                    helperText={formErrors.number_of_employees}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="notes"
                    label="Notes"
                    variant="outlined"
                    margin="normal"
                    multiline
                    rows={3}
                    onChange={handleInputChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>

                  {/* Lead Source Select */}
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Lead Source</InputLabel>
                    <Select
                      name="lead_source"
                      label="Lead Source"
                      value={formData.lead_source}
                      onChange={handleInputChange}

                    >
                      <MenuItem value="Not Selected"></MenuItem>
                      <MenuItem value="Website">Website</MenuItem>
                      <MenuItem value="Social Media">Social Media</MenuItem>
                      <MenuItem value="Campaign">Campaign</MenuItem>
                      <MenuItem value="Email">Email</MenuItem>
                      <MenuItem value="Reference">Reference</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                      <MenuItem value="AI Search">AI Search</MenuItem>
                      <MenuItem value="WebSearch">WebSearch</MenuItem>
                    </Select>
                  </FormControl>


                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Lead Status</InputLabel>
                    <Select
                      name="lead_status"
                      label="Lead Status"
                      value={formData.lead_status}
                      onChange={handleInputChange}

                    >
                      {["New", "In Progress", "Qualified"].map((status) => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>

                  {/* Lead Rating Select */}
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Lead Rating</InputLabel>
                    <Select
                      name="lead_rating"
                      label="Lead Rating"
                      value={formData.lead_rating}
                      onChange={handleInputChange}

                    >
                      <MenuItem value="Not Selected"></MenuItem>
                      <MenuItem value="Hot">Hot</MenuItem>
                      <MenuItem value="Warm">Warm</MenuItem>
                      <MenuItem value="Cold">Cold</MenuItem>
                    </Select>
                  </FormControl>

                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: SHOW_HIDDEN_CORE_CRM_FIELDS ? undefined : 'none' }}>
                  <TextField
                    fullWidth
                    name="utm_key"
                    label="UTM Key"
                    variant="outlined"
                    margin="normal"
                    onChange={handleInputChange}
                    value={formData.utm_key}
                  />
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: showTripField ? undefined : 'none' }}>
                  <Autocomplete
                    value={selectedTrip}
                    options={trips}
                    isOptionEqualToValue={(o, v) => o?.id === v?.id}
                    getOptionLabel={(option) => option?.name || ''}
                    loading={loadingTrips}
                    onInputChange={(event, newInputValue) => {
                      if (newInputValue && showTripField) {
                        fetchTrips(newInputValue);
                      }
                    }}
                    onChange={(_, newValue) => {
                        setSelectedTrip(newValue);
                        setFormData((prev) => ({ ...prev, trip_id: newValue?.id ?? null }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Trip"
                        variant="outlined"
                        margin="normal"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingTrips ? <CircularProgress size={20} /> : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    name="website_reference"
                    label="Website Reference"
                    variant="outlined"
                    margin="normal"
                    onChange={handleInputChange}
                    value={formData.website_reference}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Type</InputLabel>
                    <Select
                      name="type"
                      label="Type"
                      value={formData.type}
                      onChange={handleInputChange}

                    >
                      <MenuItem value=""></MenuItem>
                      <MenuItem value="Customer">Customer</MenuItem>
                      {showTripField && <MenuItem value="Merchant">Merchant</MenuItem>}
                      {showTripField && <MenuItem value="Guide">Guide</MenuItem>}
                      {showTripField && <MenuItem value="Creator">Creator</MenuItem>}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ display: SHOW_HIDDEN_CORE_CRM_FIELDS ? undefined : 'none' }}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Creator Type</InputLabel>
                    <Select
                      name="creator_type"
                      label="Creator Type"
                      value={formData.creator_type}
                      onChange={handleInputChange}

                    >
                      <MenuItem value=""></MenuItem>
                      <MenuItem value="Travel">Travel</MenuItem>
                      <MenuItem value="Lifestyle">Lifestyle</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth variant="outlined" margin="normal">
                    <InputLabel>Language</InputLabel>
                    <Select
                      name="language"
                      label="Language"
                      value={formData.language}
                      onChange={handleInputChange}

                    >
                      <MenuItem value="English">English</MenuItem>
                      <MenuItem value="French">French</MenuItem>
                      <MenuItem value="Spanish">Spanish</MenuItem>
                      <MenuItem value="Deutsch">Deutsch</MenuItem>
                      <MenuItem value="Italian">Italian</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>

                  <Autocomplete
                    options={agents}
                    getOptionLabel={(option) => option?.username || ''}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        name="assigned_to"
                        label="Assigned To"
                        variant="outlined"
                        margin="normal"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            height: '45px',
                            alignItems: 'center'
                          },
                          '& .MuiInputLabel-outlined': {
                            transform: 'translate(14px, 12px) scale(1)'
                          },
                          '& .MuiInputLabel-shrink': {
                            transform: 'translate(14px, -6px) scale(0.75)'
                          }
                        }}
                      />
                    )}
                    value={selectedAgent}
                    onChange={(_, newValue) => {
                      setSelectedAgent(newValue); // Simply update with new value, can be null
                    }}
                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                    freeSolo={false}
                    clearOnBlur={false}
                    clearOnEscape={true}
                    handleHomeEndKeys={true}
                    openOnFocus
                    autoComplete
                    autoHighlight
                    disableClearable={false}
                  />


                </Grid>
                <Grid item xs={12}>
                  <Autocomplete
                    multiple
                    options={products}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(option, value) =>
                      option?.id === value?.id || option?.name === value?.name
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="outlined"
                        label={showTripField ? 'Interested Packages' : 'Interested Products'}
                        placeholder={showTripField ? 'Select packages' : 'Select products'}
                      />
                    )}
                    value={selectedProducts}
                    onChange={handleProductChange}
                  />
                </Grid>

                {showPropertyRequirements && (
                  <Grid item xs={12}>
                    <MDBox mt={2}>
                      <MDTypography variant="h6" gutterBottom>
                        Property requirements
                      </MDTypography>
                      <LeadPropertyRequirements
                        bare
                        isEditing
                        values={formData}
                        onChange={(field, value) =>
                          setFormData((prev) => ({ ...prev, [field]: value }))
                        }
                      />
                    </MDBox>
                  </Grid>
                )}

                {/* Add custom fields section */}
                {customFields && customFields.length > 0 && (
                  <Grid item xs={12}>
                    <MDBox mt={2}>
                      <MDTypography variant="h6" gutterBottom>
                        Custom Fields
                      </MDTypography>
                      <CustomFieldsSection
                        ref={customFieldsRef}
                        objectType="leads"
                        objectData={formData}
                        isEditing={true}
                        onFieldUpdate={handleFieldUpdate}
                        onError={handleFieldError}
                        formErrors={formErrors}
                      />
                    </MDBox>
                  </Grid>
                )}
              </Grid>
              <MDBox mt={4} mb={1} display="flex" justifyContent="flex-end">
                <MDButton onClick={handleClose} color="secondary" sx={{ marginRight: 2 }}>
                  Close
                </MDButton>
                <MDButton
                  type="submit"
                  variant="gradient"
                  color="info"
                  disabled={isSaving} // Disable button while saving
                >
                  {isSaving ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "Save Lead"
                  )}
                </MDButton>
              </MDBox>
            </form>
          </Box>
        </Box>
      </Modal>

      {/* Add this near the end of your component return statement */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={(_, reason) => {
            if (reason === 'clickaway') return;
            setToast((t) => ({ ...t, open: false }));
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.type}
          sx={{ width: '100%', display: 'flex', alignItems: 'center' }}
          icon={toast.icon}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </>

  );
};

export default LeadFormModal;